import {createContext, type ReactNode, useContext} from 'react';
import {createPortal} from 'react-dom';

/**
 * Where overlays render: a node at the top of the app, outside every screen.
 *
 * A dialog rendered inside a screen inherits that screen's stacking context and, worse, any
 * transformed ancestor becomes its containing block — the entrance animations here use transforms,
 * so a `position: fixed` dim ended up covering only the page card, not the sidebar or the top bar.
 * Portaling into the layer sidesteps both. The layer sits INSIDE the shadow root (Layout renders it),
 * so the app's stylesheet still reaches what is portaled there; `document.body` would not do.
 */
export const LayerContext = createContext<HTMLElement | null>(null);

/** Renders `children` into the app's overlay layer — or in place until the layer has mounted. */
export const InLayer = ({children}: {children: ReactNode}) => {
    const layer = useContext(LayerContext);

    return layer === null ? children : createPortal(children, layer);
};
